// @ts-nocheck
import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Homework, HomeworkTask, HomeworkTaskType, GradingSystem } from '../../types';
import Card from './Card';
import { formatGrade, getScoreFeedbackConfig } from '../../utils/assignmentUtils';
import { CheckIcon, XMarkIcon, ArrowPathIcon, TrophyIcon, ArrowUpIcon, ArrowDownIcon, ClockIcon, BellIcon, CheckBadgeIcon, EyeIcon, PaperClipIcon, ClipboardDocumentListIcon, PlayIcon, PlusIcon, ArrowRightIcon, MicrophoneIcon, StopIcon, SpeakerWaveIcon, SparklesIcon } from './Icons';
import { formatAssetUrl } from '../../services/apiService';
import { MathMixedText } from './MathMixedText';
import { getMcqCorrectAnswerTexts, getMcqOptionLabel, homeworkSupportsMovementMode } from '../../utils/homeworkMcq';
import { HomeworkMovementMode } from './HomeworkMovementMode';

interface HomeworkViewerProps {
    homework: Homework;
    isPreview?: boolean;
    onClose?: () => void;
    onComplete?: (results: any) => void;
    showResultsInitially?: boolean;
    initialAnswers?: Record<string, any>;
    timeLimitMinutes?: number;
    /** Loại bài tập để hiển thị lời nhắc phù hợp khi xác nhận nộp bài */
    assignmentType?: 'homework' | 'task' | 'announcement';
    /** Hình thức nộp bài cho 'task' */
    submissionType?: 'none' | 'check_only' | 'photo' | 'audio_video' | 'any';
    user?: UserSettings;
    /** Thêm các nút hành động bổ sung vào Header (ví dụ: nút mở chế độ cho con) */
    renderExtraHeaderActions?: () => React.ReactNode;
    /** Trạng thái chỉ xem (không cho phép nộp bài) */
    isReadOnly?: boolean;
    /** Điểm giáo viên chấm (khi bài đã chấm) */
    teacherGrade?: number | null;
    /** Lời phê của giáo viên (khi bài đã chấm) */
    teacherComments?: string | null;
    /** Hệ thống chấm điểm để hiển thị điểm */
    gradingSystem?: GradingSystem;
    /** Có thể nộp bài theo hạn (false khi quá hạn và không cho nộp muộn) */
    canSubmitByDeadline?: boolean;
    /** Đã quá hạn nộp bài */
    isExpired?: boolean;
    isSample?: boolean;
    onCloneSample?: () => void;
}

const shuffleArray = <T,>(array: T[]): T[] => {
    if (!array) return [];
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
};

const MATCH_COLORS = [
    { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700', ring: 'ring-blue-100', dot: 'bg-blue-500', badge: 'bg-blue-600', stroke: '#3b82f6' },
    { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-700', ring: 'ring-purple-100', dot: 'bg-purple-500', badge: 'bg-purple-600', stroke: '#9333ea' },
    { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700', ring: 'ring-orange-100', dot: 'bg-orange-500', badge: 'bg-orange-600', stroke: '#f97316' },
    { bg: 'bg-pink-50', border: 'border-pink-300', text: 'text-pink-700', ring: 'ring-pink-100', dot: 'bg-pink-500', badge: 'bg-pink-600', stroke: '#ec4899' },
    { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', ring: 'ring-amber-100', dot: 'bg-amber-500', badge: 'bg-amber-600', stroke: '#f59e0b' },
    { bg: 'bg-indigo-50', border: 'border-indigo-300', text: 'text-indigo-700', ring: 'ring-indigo-100', dot: 'bg-indigo-500', badge: 'bg-indigo-600', stroke: '#6366f1' },
    { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700', ring: 'ring-emerald-100', dot: 'bg-emerald-500', badge: 'bg-emerald-600', stroke: '#10b981' },
    { bg: 'bg-rose-50', border: 'border-rose-300', text: 'text-rose-700', ring: 'ring-rose-100', dot: 'bg-rose-500', badge: 'bg-rose-600', stroke: '#f43f5e' },
];

const SUBMIT_CONFIRM_MESSAGES: Record<string, string> = {
    homework: 'Bạn có chắc chắn muốn nộp bài tập này không?\n\n• Sau khi nộp, bạn sẽ không thể chỉnh sửa.\n• Hãy kiểm tra lại các câu trả lời trước khi gửi cho giáo viên.\n• Đảm bảo đã hoàn thành đủ số câu theo yêu cầu.',
    task: 'Bạn có chắc chắn muốn xác nhận hoàn thành nhiệm vụ này không?\n\n• Hãy kiểm tra lại nội dung đã thực hiện.\n• Đảm bảo đã đính kèm ảnh/video minh chứng (nếu có yêu cầu).\n• Sau khi xác nhận, hệ thống sẽ ghi nhận kết quả cho giáo viên.',
    announcement: 'Bạn xác nhận đã xem và nắm rõ nội dung thông báo này?',
};

const SUBMIT_LABELS: Record<string, string> = {
    homework: 'NỘP BÀI TẬP',
    task: 'XÁC NHẬN HOÀN THÀNH',
    announcement: 'ĐÃ XEM THÔNG BÁO'
};

/** Đảm bảo pairs luôn là mảng (API có thể trả về object) */
const toPairsArray = (p: any): { left?: string; right?: string }[] => {
    if (!p) return [];
    if (Array.isArray(p)) return p;
    if (typeof p === 'object') return Object.values(p);
    return [];
};

const TEXT_RESPONSE_TASK_TYPES: HomeworkTaskType[] = [
    HomeworkTaskType.Questions,
    HomeworkTaskType.Writing,
    HomeworkTaskType.Research,
    HomeworkTaskType.Reading,
    HomeworkTaskType.PreClassAssignment,
    HomeworkTaskType.Drawing,
    HomeworkTaskType.ProjectFramework,
    HomeworkTaskType.Worksheet,
];

const getTaskGuidance = (task: HomeworkTask): string => {
    return String(task.content?.answerGuide || task.content?.hint || '').trim();
};

/** Tách đoạn mô tả thành (blankCount + 1) phần: hỗ trợ (1)(2)(3), ___, ..., … */
const getFillBlankParts = (description: string, blankCount: number): string[] => {
    const raw = (description || '').trim();
    if (blankCount <= 0) return [raw || ''];

    const byNumbered = raw.split(/\(\d+\)/);
    if (byNumbered.length === blankCount + 1) return byNumbered;

    const byUnderscore = raw.split(/_{3,}|…+|\.{3,}/u);
    if (byUnderscore.length === blankCount + 1) return byUnderscore;

    if (byNumbered.length > 1 && byNumbered.length <= blankCount + 1) {
        const out = [...byNumbered];
        while (out.length < blankCount + 1) out.push('');
        return out.slice(0, blankCount + 1);
    }
    if (byUnderscore.length > 1 && byUnderscore.length <= blankCount + 1) {
        const out = [...byUnderscore];
        while (out.length < blankCount + 1) out.push('');
        return out.slice(0, blankCount + 1);
    }

    return [raw, ...Array(blankCount).fill('')];
};

const countOccurrences = (arr: string[] | undefined, value: string): number =>
    (arr || []).filter((x) => String(x) === String(value)).length;

/** Gộp đáp án đúng + nhiễu, bỏ trùng (so khớp không phân biệt hoa thường, chuẩn hóa khoảng trắng). */
const uniqueFillBlankDragPool = (blanks: string[] | undefined, predefined: string[] | undefined): string[] => {
    const merged = [...(blanks || []), ...(predefined || [])]
        .map((a) => String(a ?? '').trim())
        .filter(Boolean);
    const seen = new Set<string>();
    const out: string[] = [];
    for (const a of merged) {
        const key = a.toLowerCase().replace(/\s+/g, ' ');
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(a);
    }
    return out;
};

const calculateSimilarity = (str1: string, str2: string): number => {
    const s1 = String(str1 || '').toLowerCase().replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "").trim();
    const s2 = String(str2 || '').toLowerCase().replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "").trim();
    if (!s1 || !s2) return 0;
    if (s1 === s2) return 100;

    const words1 = s1.split(/\s+/);
    const words2 = s2.split(/\s+/);
    let matches = 0;
    
    // Simple word-by-word matching
    const words2Set = new Set(words2);
    words1.forEach(w => {
        if (words2Set.has(w)) matches++;
    });

    const score = Math.round((matches / Math.max(words1.length, words2.length)) * 100);
    return score;
};

const HomeworkViewer: React.FC<HomeworkViewerProps> = ({ 
    homework, 
    isPreview, 
    onClose, 
    onComplete, 
    showResultsInitially = false, 
    initialAnswers: providedInitialAnswers, 
    timeLimitMinutes, 
    assignmentType = 'homework', 
    submissionType = 'check_only', 
    user,
    renderExtraHeaderActions,
    isReadOnly: providedIsReadOnly,
    teacherGrade,
    teacherComments,
    gradingSystem = 'numeric',
    canSubmitByDeadline = true,
    isExpired = false,
    isSample,
    onCloneSample
}) => {
    const [shuffledTasks, setShuffledTasks] = useState<HomeworkTask[]>([]);
    const [answers, setAnswers] = useState<Record<string, any>>(providedInitialAnswers || {});
    const [taskAttachments, setTaskAttachments] = useState<File[]>([]);
    const [isSubmitted, setIsSubmitted] = useState(showResultsInitially);
    const [shuffledOptions, setShuffledOptions] = useState<Record<string, string[]>>({});
    const [shuffledMatchingRight, setShuffledMatchingRight] = useState<Record<string, string[]>>({});
    const [timeLeft, setTimeLeft] = useState<number | null>(timeLimitMinutes ? timeLimitMinutes * 60 : null);
    const [zoomImage, setZoomImage] = useState<string | null>(null);
    const [dragCounterFile, setDragCounterFile] = useState(0);
    const [isDraggingFile, setIsDraggingFile] = useState(false);
    const [movementModeOpen, setMovementModeOpen] = useState(false);

    const handleDragEnterFile = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragCounterFile(prev => prev + 1);
        setIsDraggingFile(true);
    };

    const handleDragLeaveFile = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragCounterFile(prev => {
            const newVal = prev - 1;
            if (newVal <= 0) {
                setIsDraggingFile(false);
                return 0;
            }
            return newVal;
        });
    };

    const handleDragOverFile = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
        setIsDraggingFile(true);
    };

    const handleDropFile = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingFile(false);
        setDragCounterFile(0);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileAddition(Array.from(e.dataTransfer.files));
        }
    };
    
    // Limits from system settings
    const maxFiles = user?.maxFilesPerAssignment || 5;
    const maxFileSizeMB = user?.maxFileSizeMB || 10;

    // UI Refs for matching lines
    const containerRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [matchingState, setMatchingState] = useState<Record<string, { 
        activeId: string | null, 
        activeIdx: number | null,
        currentPoint: { x: number, y: number } | null,
        dragSide: 'left' | 'right' | null
    }>>({});
    const [matchingLayoutVersion, setMatchingLayoutVersion] = useState(0);

    useLayoutEffect(() => {
        setMatchingLayoutVersion(v => v + 1);
    }, [answers, shuffledTasks, homework.id]);

    const [draggedItemIdx, setDraggedItemIdx] = useState<number | null>(null);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTaskId, setRecordingTaskId] = useState<string | null>(null);
    const [taskAttempts, setTaskAttempts] = useState<Record<string, number>>({});
    const recognitionRef = useRef<any>(null);

    // Initialize Web Speech API
    useEffect(() => {
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (SpeechRecognition) {
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'vi-VN';

            recognition.onresult = (event: any) => {
                let finalTranscript = '';
                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    if (event.results[i].isFinal) {
                        finalTranscript += event.results[i][0].transcript;
                    }
                }
                if (finalTranscript && recordingTaskId) {
                    setAnswers(prev => ({
                        ...prev,
                        [recordingTaskId]: (prev[recordingTaskId] || '') + ' ' + finalTranscript
                    }));
                }
            };

            recognition.onerror = (event: any) => {
                console.error('Speech recognition error', event.error);
                setIsRecording(false);
                setRecordingTaskId(null);
            };

            recognition.onend = () => {
                setIsRecording(false);
                setRecordingTaskId(null);
            };

            recognitionRef.current = recognition;
        }
    }, [recordingTaskId]);

    const toggleRecording = (taskId: string) => {
        if (!recognitionRef.current) {
            alert('Trình duyệt của bạn không hỗ trợ nhận diện giọng nói.');
            return;
        }

        if (isRecording && recordingTaskId === taskId) {
            recognitionRef.current.stop();
        } else if (!isRecording) {
            handleAnswerChange(taskId, '');
            setRecordingTaskId(taskId);
            try {
                recognitionRef.current.start();
                setIsRecording(true);
            } catch (err) {
                console.error('Start recording failed:', err);
            }
        }
    };

    const calculateOralScore = (taskId: string, correctAnswer: string) => {
        const studentAnswer = answers[taskId] || '';
        const score = calculateSimilarity(studentAnswer, correctAnswer);
        
        // Cập nhật số lần thử
        setTaskAttempts(prev => ({
            ...prev,
            [taskId]: (prev[taskId] || 0) + 1
        }));

        return score;
    };

    const handleAnswerChange = (taskId: string, value: any) => {
        if (isSubmitted) return;
        setAnswers(prev => ({ ...prev, [taskId]: value }));
    };

    const handleFileAddition = (files: File[]) => {
        const validFiles = files.filter(f => f.size <= maxFileSizeMB * 1024 * 1024);
        if (validFiles.length < files.length) {
            alert(`Một số tệp vượt quá giới hạn dung lượng ${maxFileSizeMB}MB.`);
        }
        setTaskAttachments(prev => [...prev, ...validFiles].slice(0, maxFiles));
    };

    const doSubmit = (skipConfirm = false) => {
        if (isSubmitted) return;

        const completion = calculateCompletion();
        const threshold = 0; // Configurable

        if (assignmentType === 'homework' && completion < threshold) {
            alert(`Bạn cần hoàn thành ít nhất ${threshold}% nội dung (Hiện đạt ${completion}%).`);
            return;
        }

        if (assignmentType === 'task') {
            const hasAtt = taskAttachments.length > 0;
            if (submissionType === 'photo' && !hasAtt) {
                alert('Vui lòng đính kèm HÌNH ẢNH minh chứng.');
                return;
            } else if (submissionType === 'audio_video' && !hasAtt) {
                alert('Vui lòng đính kèm VIDEO/ÂM THANH minh chứng.');
                return;
            } else if ((submissionType === 'any') && !hasAtt) {
                alert('Vui lòng đính kèm MINH CHỨNG thực hiện.');
                return;
            }
        }

        if (!skipConfirm && !window.confirm(SUBMIT_CONFIRM_MESSAGES[assignmentType] || SUBMIT_CONFIRM_MESSAGES.homework)) return;

        setIsSubmitted(true);
        if (onComplete) {
            onComplete({ answers, attachments: taskAttachments });
        }
    };

    const calculateCompletion = () => {
        if (!homework.tasks || homework.tasks.length === 0) return 100;
        const interactiveTasks = homework.tasks.filter(t => t.taskType !== HomeworkTaskType.Instruction);
        if (interactiveTasks.length === 0) return 100;
        const answered = interactiveTasks.filter(t => {
            const a = answers[t.id];
            if (t.taskType === HomeworkTaskType.FillInBlanks) {
                const n = t.content?.blanks?.length || 0;
                if (n === 0) return true;
                if (!Array.isArray(a)) return false;
                for (let i = 0; i < n; i++) {
                    if (!String(a[i] ?? '').trim()) return false;
                }
                return true;
            }
            return a !== undefined && a !== '' && (Array.isArray(a) ? a.length > 0 : true);
        }).length;
        return Math.round((answered / interactiveTasks.length) * 100);
    };

    const calculateScore = () => {
        if (!homework.tasks) return { correct: 0, total: 0, percentage: 0 };
        const interactiveTasks = homework.tasks.filter(t => t.taskType !== HomeworkTaskType.Instruction);
        if (interactiveTasks.length === 0) return { correct: 0, total: 0, percentage: 0 };
        
        let correct = 0;
        interactiveTasks.forEach(task => {
            if (calculateScoreForTask(task, answers[task.id])) correct++;
        });

        return {
            correct,
            total: interactiveTasks.length,
            percentage: Math.round((correct / interactiveTasks.length) * 100)
        };
    };

    const score = calculateScore();
    const completionPercentage = calculateCompletion();
    const threshold = 0;
    const canSubmit = completionPercentage >= threshold;
    const isTask = assignmentType === 'task';
    const isAnnouncement = assignmentType === 'announcement';

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    // Đồng bộ answers khi initialAnswers load async (submission từ API) - chỉ khi đang xem kết quả
    useEffect(() => {
        if (showResultsInitially && providedInitialAnswers && Object.keys(providedInitialAnswers).length > 0) {
            setAnswers(prev => ({ ...prev, ...providedInitialAnswers }));
        }
    }, [showResultsInitially, providedInitialAnswers]);

    useEffect(() => {
        if (homework.tasks) {
            const shuffled = isPreview ? homework.tasks : shuffleArray(homework.tasks);
            setShuffledTasks(shuffled);

            const options: Record<string, string[]> = {};
            const matching: Record<string, string[]> = {};
            const orderingDefaults: Record<string, string[]> = {};
            
            shuffled.forEach(t => {
                if (t.content?.options) options[t.id] = shuffleArray(t.content.options);
                const pairsArr = toPairsArray(t.content?.pairs);
                if (pairsArr.length) matching[t.id] = shuffleArray(pairsArr.map((p: any) => (p && typeof p === 'object' ? p.right : p)));
                if (t.taskType === HomeworkTaskType.Ordering && t.content?.orderedItems?.length) {
                    orderingDefaults[t.id] = shuffleArray(t.content.orderedItems);
                }
                if (t.taskType === HomeworkTaskType.FillInBlanks && t.content?.isDragDrop) {
                    options[t.id] = shuffleArray(uniqueFillBlankDragPool(t.content.blanks, t.content.predefinedAnswers));
                }
            });
            setShuffledOptions(options);
            setShuffledMatchingRight(matching);
            if (Object.keys(orderingDefaults).length > 0) {
                setAnswers(prev => {
                    let changed = false;
                    const next = { ...prev };
                    Object.entries(orderingDefaults).forEach(([tid, items]) => {
                        if (!prev[tid] || (Array.isArray(prev[tid]) && prev[tid].length === 0)) {
                            next[tid] = items;
                            changed = true;
                        }
                    });
                    return changed ? next : prev;
                });
            }
        }
    }, [homework.id]);

    useEffect(() => {
        if (timeLeft === null || isSubmitted) return;
        if (timeLeft <= 0) {
            doSubmit(true);
            return;
        }
        const timer = setInterval(() => setTimeLeft(prev => (prev || 0) - 1), 1000);
        return () => clearInterval(timer);
    }, [timeLeft, isSubmitted]);

    const getTaskBadge = () => {
        if (isAnnouncement) return { label: 'Thông báo', color: 'bg-violet-100 text-violet-700 border-violet-200' };
        if (isTask) {
            switch (submissionType) {
                case 'none': return { label: 'Nhiệm vụ: Xem', color: 'bg-slate-100 text-slate-700 border-slate-200' };
                case 'check_only': return { label: 'Nhiệm vụ: Xác nhận', color: 'bg-blue-100 text-blue-700 border-blue-200' };
                case 'photo': return { label: 'Nhiệm vụ: Nộp ảnh', color: 'bg-amber-100 text-amber-700 border-amber-200' };
                case 'audio_video': return { label: 'Nhiệm vụ: Video/Âm thanh', color: 'bg-purple-100 text-purple-700 border-purple-200' };
                default: return { label: 'Nhiệm vụ: Nộp bài', color: 'bg-teal-100 text-teal-700 border-teal-200' };
            }
        }
        return { label: 'Bài tập', color: 'bg-teal-100 text-teal-700 border-teal-200' };
    };

    const taskBadge = getTaskBadge();
    const isViewOnly = providedIsReadOnly !== undefined ? providedIsReadOnly : (user?.role === 'parent' && !isPreview);
    /** Quá hạn và không cho nộp muộn → ẩn/disable các phần liên quan nộp bài */
    const isOverdueNoSubmit = isExpired && !canSubmitByDeadline;

    // --- Task Event Handlers ---
    const handleMatchingMouseDown = (e: React.MouseEvent, taskId: string, side: 'left' | 'right', id: string, idx: number) => {
        if (isSubmitted) return;
        const rect = containerRef.current?.getBoundingClientRect();
        setMatchingState(prev => ({
            ...prev,
            [taskId]: {
                activeId: id,
                activeIdx: idx,
                currentPoint: { x: e.clientX - (rect?.left || 0), y: e.clientY - (rect?.top || 0) },
                dragSide: side
            }
        }));
    };

    const handleMatchingMouseUp = (e: React.MouseEvent, taskId: string, side: 'left' | 'right', id: string, idx: number) => {
        const state = matchingState[taskId];
        if (!state?.activeId || state.dragSide === side) {
            setMatchingState(prev => ({ ...prev, [taskId]: { activeId: null, activeIdx: null, currentPoint: null, dragSide: null } }));
            return;
        }

        const currentAnswers = answers[taskId] || [];
        const left = state.dragSide === 'left' ? state.activeId : id;
        const right = state.dragSide === 'right' ? state.activeId : id;

        const newAnswers = currentAnswers.filter((m: any) => m.left !== left && m.right !== right);
        newAnswers.push({ left, right });
        handleAnswerChange(taskId, newAnswers);
        setMatchingState(prev => ({ ...prev, [taskId]: { activeId: null, activeIdx: null, currentPoint: null, dragSide: null } }));
    };

    const handleMatchingMouseMove = (e: React.MouseEvent, taskId: string) => {
        const state = matchingState[taskId];
        if (!state?.activeId) return;
        const rect = containerRef.current?.getBoundingClientRect();
        setMatchingState(prev => ({
            ...prev,
            [taskId]: { ...state, currentPoint: { x: e.clientX - (rect?.left || 0), y: e.clientY - (rect?.top || 0) } }
        }));
    };

    const handleOrderDragStart = (e: React.DragEvent, idx: number) => {
        if (isSubmitted) return;
        setDraggedItemIdx(idx);
    };

    const handleOrderDragOver = (e: React.DragEvent, idx: number) => {
        e.preventDefault();
    };

    const handleOrderDrop = (taskId: string, idx: number) => {
        if (draggedItemIdx === null) return;
        const current = [...(answers[taskId] || [])];
        const item = current.splice(draggedItemIdx, 1)[0];
        current.splice(idx, 0, item);
        handleAnswerChange(taskId, current);
        setDraggedItemIdx(null);
    };

    const handleFillBlankDragStart = (e: React.DragEvent, value: string) => {
        if (isSubmitted) return;
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData('text/plain', value);
    };

    const handleFillBlankDrop = (taskId: string, blankIdx: number, value: string, slotCount: number) => {
        if (isSubmitted) return;
        const current = Array.isArray(answers[taskId]) ? [...answers[taskId]] : [];
        while (current.length < slotCount) current.push('');
        if (blankIdx >= 0 && blankIdx < slotCount) current[blankIdx] = value;
        handleAnswerChange(taskId, current);
    };

    const renderMatchingLines = (taskId: string, task: HomeworkTask) => {
        const currentAnswers = answers[taskId] || [];
        const state = matchingState[taskId];
        const containerEl = document.getElementById(`matching-container-${taskId}`);
        const rect = containerEl?.getBoundingClientRect();

        return (
            <svg className="absolute inset-0 pointer-events-none z-[5]" style={{ width: '100%', height: '100%' }}>
                {currentAnswers.map((m: any, idx: number) => {
                    const color = MATCH_COLORS[idx % MATCH_COLORS.length];
                    const pairsForTask = toPairsArray(task.content?.pairs);
                    const leftIdx = pairsForTask.findIndex((p: any) => p?.left === m.left);
                    const rightIdx = shuffledMatchingRight[taskId]?.indexOf(m.right);
                    const leftEl = document.getElementById(`match-${taskId}-left-${leftIdx}`);
                    const rightEl = document.getElementById(`match-${taskId}-right-${rightIdx}`);
                    if (!leftEl || !rightEl || !rect || leftIdx < 0 || rightIdx < 0) return null;

                    const lRect = leftEl.getBoundingClientRect();
                    const rRect = rightEl.getBoundingClientRect();
                    const x1 = lRect.right - rect.left;
                    const y1 = lRect.top + lRect.height / 2 - rect.top;
                    const x2 = rRect.left - rect.left;
                    const y2 = rRect.top + rRect.height / 2 - rect.top;

                    return <line key={idx} x1={x1} y1={y1} x2={x2} y2={y2} stroke={(color as any).stroke || '#14b8a6'} strokeWidth="3" strokeDasharray="5,5" opacity="0.8" />;
                })}
                {state?.activeId && state.currentPoint && rect && (
                    (() => {
                        const leftEl = state.dragSide === 'left' ? document.getElementById(`match-${taskId}-left-${state.activeIdx}`) : null;
                        const rightEl = state.dragSide === 'right' ? document.getElementById(`match-${taskId}-right-${state.activeIdx}`) : null;
                        const x1 = state.dragSide === 'left' && leftEl ? leftEl.getBoundingClientRect().right - rect.left : state.currentPoint.x;
                        const y1 = state.dragSide === 'left' && leftEl ? leftEl.getBoundingClientRect().top + leftEl.getBoundingClientRect().height / 2 - rect.top : state.currentPoint.y;
                        const x2 = state.dragSide === 'right' && rightEl ? rightEl.getBoundingClientRect().left - rect.left : state.currentPoint.x;
                        const y2 = state.dragSide === 'right' && rightEl ? rightEl.getBoundingClientRect().top + rightEl.getBoundingClientRect().height / 2 - rect.top : state.currentPoint.y;
                        return <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#34d399" strokeWidth="3" strokeDasharray="5,5" />;
                    })()
                )}
            </svg>
        );
    };

    const renderTaskContent = (task: HomeworkTask) => {
        const taskId = task.id;
        const currentAnswer = answers[taskId];

        switch (task.taskType) {
            case HomeworkTaskType.MultipleChoiceSingle:
            case HomeworkTaskType.MultipleChoiceMulti: {
                const mcqCorrectTexts = getMcqCorrectAnswerTexts(task.content, task.content?.options || []);
                return (
                    <div className="grid gap-3 sm:grid-cols-2">
                        {(shuffledOptions[taskId] || []).map((option, optIdx) => {
                            const isSelected = task.taskType === HomeworkTaskType.MultipleChoiceMulti ? (currentAnswer || []).includes(option) : currentAnswer === option;
                            const isCorrect = mcqCorrectTexts.includes(String(option));
                            let style = 'border-slate-100 hover:border-teal-300 hover:bg-teal-50/30';
                            if (isSubmitted) {
                                if (isCorrect) style = 'border-emerald-500 bg-emerald-50 text-emerald-900 ring-1 ring-emerald-500';
                                else if (isSelected) style = 'border-rose-500 bg-rose-50 text-rose-900';
                                else style = 'opacity-60 border-slate-50 grayscale-[0.5]';
                            } else if (isSelected) style = 'border-teal-500 bg-teal-50 ring-2 ring-teal-500/20';

                            return (
                                <button key={optIdx} disabled={isSubmitted} onClick={() => {
                                    if (task.taskType === HomeworkTaskType.MultipleChoiceMulti) {
                                        const current = answers[taskId] || [];
                                        handleAnswerChange(taskId, current.includes(option) ? current.filter(o => o !== option) : [...current, option]);
                                    } else handleAnswerChange(taskId, option);
                                }} className={`p-5 rounded-[1.5rem] border-2 text-left transition-all flex items-start gap-3 shadow-sm ${style}`}>
                                    <span className="mt-1 flex-shrink-0 inline-flex h-7 min-w-[1.5rem] items-center justify-center rounded-lg bg-slate-100 text-xs font-black text-slate-500 tabular-nums" title="Thứ tự trên màn hình (sau khi xáo trộn, A/B chỉ là thứ tự hiển thị)">
                                        {getMcqOptionLabel(optIdx)}
                                    </span>
                                    <div className={`mt-1 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-teal-600 bg-teal-600' : 'border-slate-300 bg-white'}`}>
                                        {isSelected && <CheckIcon className="h-3 w-3 text-white stroke-[4px]" />}
                                    </div>
                                    <MathMixedText text={String(option)} className="font-bold text-sm lg:text-base leading-tight text-slate-700" />
                                </button>
                            );
                        })}
                    </div>
                );
            }

            case HomeworkTaskType.TrueFalse:
                return (
                    <div className="flex gap-4">
                        {['Đúng', 'Sai'].map(val => {
                            const isSelected = currentAnswer === val;
                            const isCorrect = task.content?.correctAnswer === val;
                            let style = 'border-slate-100 hover:bg-slate-50';
                            if (isSubmitted) {
                                if (isCorrect) style = 'border-emerald-500 bg-emerald-50 text-emerald-900 ring-1 ring-emerald-500';
                                else if (isSelected) style = 'border-rose-500 bg-rose-50 text-rose-900';
                                else style = 'opacity-60 border-slate-50';
                            } else if (isSelected) style = 'border-teal-500 bg-teal-50 ring-2 ring-teal-500/20';

                            return (
                                <button key={val} disabled={isSubmitted} onClick={() => handleAnswerChange(taskId, val)} className={`flex-1 p-5 rounded-2xl border-2 font-black transition-all uppercase tracking-widest ${style}`}>
                                    {val === 'Đúng' ? '✅ ' : '❌ '}{val}
                                </button>
                            );
                        })}
                    </div>
                );

            case HomeworkTaskType.Matching:
                return (
                    <div id={`matching-container-${taskId}`} className="relative min-h-[300px]">
                        <div className="flex justify-between items-center mb-6">
                            <span className="text-[11px] font-bold text-slate-400 italic">Nhấn & Kéo các thẻ để nối cặp</span>
                            {!isSubmitted && (currentAnswer || []).length > 0 && (
                                <button onClick={() => handleAnswerChange(taskId, [])} className="text-[11px] font-black text-rose-500 hover:underline uppercase tracking-tighter">Xóa tất cả</button>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-12 lg:gap-20 relative z-10">
                            <div className="space-y-4">
                                {toPairsArray(task.content?.pairs).map((pair, pIdx) => {
                                    const leftVal = pair?.left ?? pair;
                                    const matchIdx = (currentAnswer || []).findIndex(m => m.left === leftVal);
                                    const isMatched = matchIdx !== -1;
                                    const color = isMatched ? MATCH_COLORS[matchIdx % MATCH_COLORS.length] : null;
                                    return (
                                        <div key={pIdx} id={`match-${taskId}-left-${pIdx}`} 
                                            onMouseDown={e => handleMatchingMouseDown(e, taskId, 'left', leftVal, pIdx)}
                                            onMouseUp={e => handleMatchingMouseUp(e, taskId, 'left', leftVal, pIdx)}
                                            className={`p-4 text-sm font-bold border-2 rounded-2xl bg-white cursor-crosshair transition-all shadow-sm flex justify-between items-center ${isMatched ? `${color.bg} ${color.border} ${color.text}` : 'border-slate-100 hover:border-teal-200'}`}>
                                            <span className="flex-1 min-w-0 text-left"><MathMixedText text={String(leftVal ?? '')} /></span>
                                            <div className={`w-3 h-3 rounded-full ${isMatched ? color.dot : 'bg-slate-200'}`} />
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="space-y-4">
                                {(shuffledMatchingRight[taskId] || []).map((rightVal, rIdx) => {
                                    const matchIdx = (currentAnswer || []).findIndex(m => m.right === rightVal);
                                    const isMatched = matchIdx !== -1;
                                    const color = isMatched ? MATCH_COLORS[matchIdx % MATCH_COLORS.length] : null;
                                    return (
                                        <div key={rIdx} id={`match-${taskId}-right-${rIdx}`}
                                            onMouseDown={e => handleMatchingMouseDown(e, taskId, 'right', rightVal, rIdx)}
                                            onMouseUp={e => handleMatchingMouseUp(e, taskId, 'right', rightVal, rIdx)}
                                            className={`p-4 text-sm font-bold border-2 rounded-2xl bg-white cursor-crosshair transition-all shadow-sm flex justify-between items-center ${isMatched ? `${color.bg} ${color.border} ${color.text}` : 'border-slate-100 hover:border-teal-200'}`}>
                                            <div className={`w-3 h-3 rounded-full ${isMatched ? color.dot : 'bg-slate-200'}`} />
                                            <span className="flex-1 min-w-0 text-right"><MathMixedText text={String(rightVal ?? '')} /></span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {renderMatchingLines(taskId, task)}
                    </div>
                );

            case HomeworkTaskType.Ordering:
                return (
                    <div className="space-y-3 max-w-md mx-auto">
                        {(currentAnswer || []).map((item, i) => (
                            <div key={item} draggable={!isSubmitted}
                                onDragStart={e => handleOrderDragStart(e, i)}
                                onDragOver={e => handleOrderDragOver(e, i)}
                                onDrop={() => handleOrderDrop(taskId, i)}
                                className={`flex items-center gap-4 p-4 bg-white border-2 rounded-2xl shadow-sm transition-all ${isSubmitted ? 'border-slate-100' : 'border-teal-50 hover:border-teal-300 cursor-grab active:cursor-grabbing'} ${draggedItemIdx === i ? 'opacity-40 scale-95' : 'opacity-100'}`}>
                                <span className="w-8 h-8 rounded-xl bg-teal-600 text-white flex items-center justify-center text-xs font-black shadow-lg shadow-teal-500/20">{i + 1}</span>
                                <span className="flex-1 min-w-0 text-sm font-bold text-slate-700"><MathMixedText text={String(item)} /></span>
                                {!isSubmitted && <div className="flex flex-col gap-1 text-slate-300"><div className="w-4 h-0.5 bg-current" /><div className="w-4 h-0.5 bg-current" /></div>}
                            </div>
                        ))}
                    </div>
                );

            case HomeworkTaskType.OralResponse:
                const correctText = task.content?.correctAnswer || '';
                const maxAttempts = task.content?.maxAttempts || 3;
                const attempts = taskAttempts[taskId] || 0;
                const canRetry = attempts < maxAttempts;
                const similarity = calculateSimilarity(currentAnswer, correctText);
                const isCorrect = similarity >= 80;

                return (
                    <div className="space-y-6 animate-in fade-in duration-500">
                        {/* Media Section */}
                        <div className="flex flex-wrap gap-4 items-start">
                            {task.audioUrl && (
                                <div className="flex-1 min-w-[200px] p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100 flex flex-col gap-2">
                                    <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest flex items-center gap-2">
                                        <SpeakerWaveIcon className="h-3 w-3" /> Nghe âm thanh
                                    </span>
                                    <audio src={formatAssetUrl(task.audioUrl)} controls className="w-full h-8" />
                                </div>
                            )}
                            {task.videoUrl && (
                                <div className="flex-1 min-w-[300px] p-4 bg-slate-900 rounded-2xl border border-slate-800 flex flex-col gap-2 overflow-hidden shadow-2xl">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                        <PlayIcon className="h-3 w-3" /> Xem video
                                    </span>
                                    <div className="aspect-video rounded-xl overflow-hidden bg-black">
                                        <video src={formatAssetUrl(task.videoUrl)} controls className="w-full h-full" />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Recording Section */}
                        <div className="relative group">
                            <div className={`p-8 rounded-[2rem] border-4 border-dashed transition-all flex flex-col items-center justify-center gap-6 ${
                                isRecording && recordingTaskId === taskId 
                                ? 'border-rose-500 bg-rose-50 shadow-2xl animate-pulse' 
                                : (currentAnswer ? 'border-emerald-500 bg-emerald-50/30' : 'border-slate-200 bg-white hover:border-indigo-300')
                            }`}>
                                <button 
                                    disabled={isSubmitted || (!canRetry && !isRecording)}
                                    onClick={() => toggleRecording(taskId)}
                                    className={`w-24 h-24 rounded-full flex items-center justify-center transition-all shadow-2xl relative ${
                                        isRecording && recordingTaskId === taskId
                                        ? 'bg-rose-600 text-white scale-110 ring-8 ring-rose-100'
                                        : (currentAnswer ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-indigo-600 text-white hover:bg-indigo-700 hover:scale-105 active:scale-95')
                                    } ${(!canRetry && !isRecording && !isSubmitted) ? 'opacity-50 grayscale cursor-not-allowed' : ''}`}
                                >
                                    {isRecording && recordingTaskId === taskId ? (
                                        <StopIcon className="h-10 w-10" />
                                    ) : (
                                        <MicrophoneIcon className="h-10 w-10" />
                                    )}
                                    {isRecording && recordingTaskId === taskId && (
                                        <div className="absolute inset-0 rounded-full border-4 border-white animate-ping opacity-20" />
                                    )}
                                </button>

                                <div className="text-center space-y-2">
                                    <h4 className="text-xl font-black text-slate-800">
                                        {isRecording && recordingTaskId === taskId ? 'Đang lắng nghe con nói...' : (currentAnswer ? 'Đã ghi nhận câu trả lời' : 'Nhấn để bắt đầu nói')}
                                    </h4>
                                    {!isSubmitted && (
                                        <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                                            Số lần thử: <span className="text-indigo-600">{attempts}</span> / {maxAttempts}
                                        </p>
                                    )}
                                </div>

                                {currentAnswer && (
                                    <div className="w-full max-w-lg p-6 bg-white/80 backdrop-blur-md rounded-2xl border-2 border-slate-100 shadow-inner text-center animate-in zoom-in-95">
                                        <p className="text-lg font-bold text-slate-700 italic">"{currentAnswer}"</p>
                                    </div>
                                )}

                                {currentAnswer && !isRecording && !isSubmitted && (
                                    <div className="flex flex-col items-center gap-4 w-full">
                                        <div className="flex items-center gap-3">
                                            <div className="h-2 w-48 bg-slate-100 rounded-full overflow-hidden">
                                                <div className={`h-full transition-all duration-1000 ${isCorrect ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${similarity}%` }}></div>
                                            </div>
                                            <span className={`text-sm font-black ${isCorrect ? 'text-emerald-600' : 'text-amber-600'}`}>{similarity}%</span>
                                        </div>
                                        
                                        {!isCorrect && canRetry && (
                                            <button 
                                                onClick={() => {
                                                    setTaskAttempts(prev => ({ ...prev, [taskId]: (prev[taskId] || 0) + 1 }));
                                                    toggleRecording(taskId);
                                                }}
                                                className="px-6 py-2 bg-amber-100 text-amber-700 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-amber-200 transition-all flex items-center gap-2"
                                            >
                                                <ArrowPathIcon className="h-4 w-4" /> Thử lại lần nữa
                                            </button>
                                        )}
                                        {isCorrect && (
                                            <div className="flex items-center gap-2 text-emerald-600 animate-bounce">
                                                <CheckBadgeIcon className="h-6 w-6" />
                                                <span className="text-sm font-black uppercase tracking-widest">Tuyệt vời! Con nói rất tốt</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );

            case HomeworkTaskType.Questions:
            case HomeworkTaskType.Writing:
            case HomeworkTaskType.Research:
            case HomeworkTaskType.Reading:
            case HomeworkTaskType.PreClassAssignment:
            case HomeworkTaskType.Drawing:
            case HomeworkTaskType.ProjectFramework:
            case HomeworkTaskType.Worksheet:
                return (
                    <textarea disabled={isSubmitted} value={currentAnswer || ''}
                        onChange={e => handleAnswerChange(taskId, e.target.value)}
                        className="w-full p-6 rounded-2xl border-2 border-slate-100 bg-slate-50/30 focus:bg-white focus:border-teal-500 outline-none transition-all resize-none min-h-[150px] font-medium text-slate-700 shadow-inner"
                        placeholder="Con hãy nhập nội dung trả lời tại đây..." />
                );

            case HomeworkTaskType.FillInBlanks: {
                const blankCount = task.content?.blanks?.length ?? 0;
                const parts = getFillBlankParts(task.description, blankCount);
                const isDragDrop = task.content?.isDragDrop;
                const blankOptions = shuffledOptions[taskId] || [];

                return (
                    <div className="space-y-6">
                        <div className="bg-slate-50/50 p-5 sm:p-6 rounded-2xl border border-slate-100 shadow-inner text-base sm:text-[1.05rem] leading-[1.65] text-slate-700 font-bold [overflow-wrap:anywhere]">
                            {parts.map((part, i, arr) => (
                                <React.Fragment key={i}>
                                    <span className="align-middle"><MathMixedText text={part} whitespaceMode="line" /></span>
                                    {i < arr.length - 1 && (
                                        isDragDrop ? (
                                            <span
                                                onDragOver={e => !isSubmitted && e.preventDefault()}
                                                onDragEnter={e => !isSubmitted && e.preventDefault()}
                                                onDrop={e => {
                                                    const val = e.dataTransfer.getData('text/plain');
                                                    handleFillBlankDrop(taskId, i, val, blankCount);
                                                }}
                                                className={`mx-0.5 inline-flex min-h-[1.45em] min-w-[2.75rem] max-w-[min(100%,14rem)] shrink-0 align-middle items-center justify-center gap-0.5 rounded-md border-2 border-dashed px-1.5 py-px text-center text-sm font-black leading-snug transition-all sm:min-w-[3.25rem] sm:px-2 sm:text-[0.95rem] ${
                                                    isSubmitted
                                                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                                                        : currentAnswer?.[i]
                                                            ? 'border-solid border-teal-500 bg-white text-teal-700 shadow-sm'
                                                            : 'border-slate-300 bg-white/60 text-slate-400'
                                                }`}
                                            >
                                                <span className="max-w-full break-words text-left [hyphens:auto]">
                                                    {currentAnswer?.[i] ? <MathMixedText text={String(currentAnswer[i])} /> : '…'}
                                                </span>
                                                {!isSubmitted && currentAnswer?.[i] && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const next = Array.isArray(currentAnswer) ? [...currentAnswer] : [];
                                                            while (next.length < blankCount) next.push('');
                                                            next[i] = '';
                                                            handleAnswerChange(taskId, next);
                                                        }}
                                                        className="inline-flex shrink-0 text-slate-300 hover:text-rose-500 transition-colors"
                                                        aria-label="Xóa ô"
                                                    >
                                                        <XMarkIcon className="h-3 w-3" />
                                                    </button>
                                                )}
                                            </span>
                                        ) : (
                                            <input
                                                type="text"
                                                disabled={isSubmitted}
                                                value={(currentAnswer || [])[i] || ''}
                                                onChange={e => {
                                                    const next = Array.isArray(currentAnswer) ? [...currentAnswer] : [];
                                                    while (next.length < blankCount) next.push('');
                                                    next[i] = e.target.value;
                                                    handleAnswerChange(taskId, next);
                                                }}
                                                className={`mx-0.5 inline-block w-[6.5rem] max-w-[40vw] align-middle rounded-md border-b-2 bg-white/40 px-1 py-0.5 text-center text-sm font-black focus:border-teal-600 focus:outline-none sm:w-36 sm:text-[0.95rem] ${isSubmitted ? 'border-emerald-500 text-emerald-700' : 'border-teal-400'}`}
                                                placeholder="…"
                                            />
                                        )
                                    )}
                                </React.Fragment>
                            ))}
                        </div>

                        {isDragDrop && !isSubmitted && blankOptions.length > 0 && (
                            <div className="p-4 bg-amber-50/30 rounded-2xl border border-amber-100/50">
                                <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-3 px-2">Kéo đáp án vào ô trống</p>
                                <div className="flex flex-wrap gap-2">
                                    {blankOptions.map((opt, idx) => {
                                        const placed = countOccurrences(currentAnswer as string[] | undefined, opt);
                                        const rankAmongSame = blankOptions.slice(0, idx + 1).filter((x) => x === opt).length;
                                        const isUsed = placed >= rankAmongSame;
                                        return (
                                            <div 
                                                key={idx}
                                                draggable={!isSubmitted && !isUsed}
                                                onDragStart={e => handleFillBlankDragStart(e, opt)}
                                                onClick={() => {
                                                    if (isSubmitted || isUsed) return;
                                                    const next = Array.isArray(currentAnswer) ? [...currentAnswer] : [];
                                                    while (next.length < blankCount) next.push('');
                                                    const firstEmpty = next.findIndex((v, bi) => bi < blankCount && !String(v ?? '').trim());
                                                    if (firstEmpty !== -1) {
                                                        next[firstEmpty] = opt;
                                                        handleAnswerChange(taskId, next);
                                                    }
                                                }}
                                                className={`px-4 py-2 bg-white border-2 rounded-xl text-sm font-bold shadow-sm transition-all select-none ${
                                                    isUsed 
                                                        ? 'opacity-40 border-slate-100 text-slate-300 cursor-not-allowed' 
                                                        : 'border-amber-100 text-slate-700 hover:border-amber-400 hover:shadow-md cursor-grab active:cursor-grabbing'
                                                }`}
                                            >
                                                <MathMixedText text={String(opt)} />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                );
            }

            default: return null;
        }
    };

    return (
        <div className="fixed inset-0 z-[100] bg-slate-50 overflow-y-auto">
            <div ref={containerRef} className={`space-y-6 lg:space-y-8 max-w-4xl mx-auto p-4 ${isViewOnly ? 'pb-12' : 'pb-40'} relative min-h-screen`}
                onMouseMove={e => Object.keys(matchingState).forEach(tid => handleMatchingMouseMove(e, tid))}
                onMouseUp={() => setMatchingState({})}>
                
                {/* 1. Header Navigation Bar */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between bg-white p-5 lg:p-6 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 sticky top-0 lg:top-4 z-50 gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                            <span className={`px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider border ${taskBadge.color}`}>{taskBadge.label}</span>
                            {timeLeft !== null && !isSubmitted && !isViewOnly && !isOverdueNoSubmit && (
                                <span className={`px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider border ${timeLeft < 60 ? 'bg-rose-50 border-rose-200 text-rose-600 animate-pulse' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
                                    <ClockIcon className="h-3 w-3 inline mr-1" /> {formatTime(timeLeft)}
                                </span>
                            )}
                            {isViewOnly && <span className="px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider border bg-slate-100 border-slate-200 text-slate-500 flex items-center gap-1.5"><EyeIcon className="h-3 w-3" /> Chế độ xem</span>}
                            {isSample && <span className="px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider border bg-amber-500 border-amber-600 text-white flex items-center gap-1.5 shadow-sm"><SparklesIcon className="h-3 w-3 animate-pulse" /> Dữ liệu mẫu</span>}
                        </div>
                        <h2 className="text-lg lg:text-2xl font-black text-slate-900 leading-tight truncate">{homework?.title || 'Nhiệm vụ của con'}</h2>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 justify-end">
                        {homeworkSupportsMovementMode(shuffledTasks) && !isViewOnly && !isSubmitted && !isOverdueNoSubmit && !isAnnouncement && (
                            <button
                                type="button"
                                onClick={() => setMovementModeOpen(true)}
                                className="shrink-0 rounded-2xl border-2 border-violet-200 bg-violet-50 px-3 py-2 text-[10px] font-black uppercase tracking-wider text-violet-800 shadow-sm transition hover:bg-violet-100 active:scale-95"
                                title="Mở camera, chọn đáp án bằng tư thế hoặc cử chỉ"
                            >
                                🤸 Làm bài kết hợp vận động
                            </button>
                        )}
                        {renderExtraHeaderActions && renderExtraHeaderActions()}
                        {!isSubmitted && !isViewOnly && !isOverdueNoSubmit && (isTask || !isAnnouncement) && (
                            <div className="hidden sm:flex items-center gap-3 bg-slate-50 px-4 py-2 rounded-2xl border border-slate-100">
                                <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                                    <div className={`h-full transition-all duration-500 ${completionPercentage >= threshold ? 'bg-emerald-500' : 'bg-teal-500'}`} style={{ width: `${completionPercentage}%` }}></div>
                                </div>
                                <p className="text-[10px] font-black text-slate-500 whitespace-nowrap">{completionPercentage}%</p>
                            </div>
                        )}
                        {isSample && onCloneSample && (
                            <button 
                                onClick={onCloneSample} 
                                className="px-4 py-2.5 bg-teal-600 text-white hover:bg-teal-700 rounded-2xl transition-all active:scale-95 flex items-center gap-2 shadow-lg shadow-teal-100"
                            >
                                <SparklesIcon className="h-5 w-5 text-white animate-pulse" />
                                <span className="text-xs font-black uppercase tracking-widest">Sửa thành của tôi</span>
                            </button>
                        )}
                        {onClose && <button onClick={onClose} className="p-2 lg:px-4 lg:py-2.5 bg-slate-100 text-slate-600 hover:bg-rose-50 hover:text-rose-600 rounded-2xl transition-all active:scale-95 flex items-center gap-2 group">
                            <XMarkIcon className="h-5 w-5 group-hover:rotate-90 transition-transform" /><span className="hidden sm:inline text-xs font-black uppercase tracking-widest">Đóng</span>
                        </button>}
                    </div>
                </div>

                {/* 2a. Quá hạn nộp bài - Thông báo */}
                {!isViewOnly && !isSubmitted && isExpired && !canSubmitByDeadline && (
                    <div className="p-4 rounded-2xl bg-rose-50 border-2 border-rose-200 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center shrink-0">
                            <ClockIcon className="h-5 w-5 text-rose-600" />
                        </div>
                        <div>
                            <p className="font-black text-rose-800 uppercase tracking-tight">Đã quá hạn nộp bài</p>
                            <p className="text-sm text-rose-600 mt-0.5">Giáo viên không cho phép nộp muộn. Bạn chỉ có thể xem lại nội dung bài tập.</p>
                        </div>
                    </div>
                )}

                {/* 2. Unified Instructions & Content Header */}
                <div className={`p-6 lg:p-8 rounded-[2.5rem] border-2 shadow-2xl shadow-slate-200/50 relative overflow-hidden ${isAnnouncement ? 'bg-violet-50 border-violet-100' : 'bg-white border-slate-100'}`}>
                    <div className={`absolute top-0 right-0 w-48 h-48 -mr-16 -mt-16 opacity-5 rounded-full ${isAnnouncement ? 'bg-violet-500' : 'bg-teal-500'}`} />
                    <div className="relative z-10 flex flex-col md:flex-row gap-6 lg:gap-8">
                        <div className={`w-16 h-16 rounded-[1.5rem] flex items-center justify-center shrink-0 shadow-lg ${isAnnouncement ? 'bg-violet-600' : 'bg-teal-600'}`}>
                            {isAnnouncement ? <BellIcon className="h-8 w-8 text-white" /> : <ClipboardDocumentListIcon className="h-8 w-8 text-white" />}
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className={`text-xs font-black uppercase tracking-[0.2em] ${isAnnouncement ? 'text-violet-800' : 'text-teal-800'}`}>{isAnnouncement ? 'Thông báo từ giáo viên' : 'Hướng dẫn & Nội dung chi tiết'}</h3>
                                {isTask && !isViewOnly && !isOverdueNoSubmit && (
                                    <div className="flex flex-wrap gap-2">
                                        {homework?.allowLateSubmission && <span className="px-2 py-1 bg-emerald-50 text-emerald-600 rounded-lg text-[8px] font-black uppercase border border-emerald-100">Nộp muộn OK</span>}
                                        {homework?.isMandatory && <span className="px-2 py-1 bg-rose-50 text-rose-600 rounded-lg text-[8px] font-black uppercase border border-rose-100">Bắt buộc</span>}
                                    </div>
                                )}
                            </div>
                            <div className={`text-slate-700 text-lg font-medium leading-relaxed whitespace-pre-line ${isAnnouncement ? 'bg-white/40 border-white/60' : 'bg-slate-50/50 border-slate-100'} p-5 lg:p-6 rounded-3xl border backdrop-blur-sm`}>
                                {homework?.generalInstructions || homework?.content || 'Con hãy xem kỹ nội dung và hoàn thành theo yêu cầu của giáo viên nhé!'}
                            </div>
                            {isTask && !isViewOnly && !isOverdueNoSubmit && (
                                <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-white rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 shadow-sm border border-slate-100">
                                    <div className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" />
                                    Yêu cầu nộp: <span className="text-teal-600 ml-1">{submissionType === 'none' ? 'Chỉ cần xem' : submissionType === 'check_only' ? 'Xác nhận hoàn thành' : submissionType === 'photo' ? 'Chụp ảnh minh chứng' : submissionType === 'audio_video' ? 'Quay video/Ghi âm' : 'Nộp bài trực tiếp'}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* 3. Teacher's Attachments */}
                {homework?.attachments && homework.attachments.length > 0 && (
                    <Card className="p-6 bg-white border border-slate-100 shadow-xl shadow-slate-200/30 rounded-[2.5rem] overflow-hidden">
                        <div className="flex items-center gap-3 mb-6"><div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center"><PaperClipIcon className="h-5 w-5 text-teal-600" /></div><h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">Tài liệu tham khảo từ giáo viên</h3></div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {homework.attachments.map((att, idx) => (
                                <div key={idx} className="group relative bg-slate-50 border border-slate-100 rounded-2xl p-2 transition-all hover:border-teal-200 hover:shadow-md">
                                    {att.type === 'image' ? (
                                        <div className="aspect-square rounded-xl overflow-hidden mb-2 bg-white border border-slate-100 relative group/img">
                                            <img src={formatAssetUrl(att.url)} alt={att.name} className="w-full h-full object-cover cursor-zoom-in transition-transform duration-500 group-hover/img:scale-110" onClick={() => setZoomImage(formatAssetUrl(att.url))} />
                                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center pointer-events-none"><EyeIcon className="h-6 w-6 text-white" /></div>
                                        </div>
                                    ) : (
                                        <div className="aspect-square rounded-xl overflow-hidden mb-2 bg-white border border-slate-100 flex flex-col items-center justify-center gap-2 p-2 text-center relative group/file">
                                            <span className="text-3xl">{att.type === 'video' ? '🎬' : att.type === 'audio' ? '🎵' : '📄'}</span>
                                            {att.type === 'video' && <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/file:opacity-100 transition-opacity flex items-center justify-center cursor-pointer z-10" onClick={() => window.open(formatAssetUrl(att.url), '_blank')}><PlayIcon className="h-8 w-8 text-white" /></div>}
                                            {att.type === 'audio' && <audio controls className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10"><source src={formatAssetUrl(att.url)} type="audio/mpeg" /></audio>}
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between px-1"><p className="text-[10px] font-bold text-slate-700 truncate flex-1">{att.name}</p><button onClick={() => { const a = document.createElement('a'); a.href = formatAssetUrl(att.url); a.download = att.name; document.body.appendChild(a); a.click(); document.body.removeChild(a); }} className="p-1.5 text-slate-400 hover:text-teal-600 transition-colors"><ArrowDownIcon className="h-3 w-3" /></button></div>
                                </div>
                            ))}
                        </div>
                    </Card>
                )}

                {/* 4. Results Section - 1 khối gộp: Điểm + lời phê + khích lệ, thiết kế hiện đại */}
                {isSubmitted && (score.total > 0 || teacherGrade != null || (teacherComments && teacherComments.trim())) && (() => {
                    const numScore = teacherGrade != null ? Number(teacherGrade) : (score.percentage / 10);
                    const cfg = getScoreFeedbackConfig(numScore);
                    return (
                        <div className={`bg-gradient-to-br ${cfg.gradient} rounded-[2rem] lg:rounded-[2.5rem] shadow-2xl ${cfg.shadow} overflow-hidden animate-in slide-in-from-top-4 duration-500`}>
                            <div className="p-6 lg:p-8 flex flex-col lg:flex-row gap-6 lg:gap-8">
                                {/* Cột trái: Điểm + Lời phê cạnh nhau */}
                                <div className="flex-1 flex flex-col sm:flex-row gap-4 sm:gap-6 items-start">
                                    <div className="flex items-center gap-3 shrink-0">
                                        <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30">
                                            <TrophyIcon className="h-7 w-7 text-white" />
                                        </div>
                                        <div>
                                            {teacherGrade != null && gradingSystem !== 'none' && (
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-white/80 text-sm font-bold">Điểm</span>
                                                    <span className="text-3xl lg:text-4xl font-black text-white">{formatGrade(teacherGrade, gradingSystem)}</span>
                                                </div>
                                            )}
                                            {score.total > 0 && !(teacherGrade != null && gradingSystem !== 'none') && (
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-3xl lg:text-4xl font-black text-white">{score.correct}/{score.total}</span>
                                                    <span className="text-white/80 text-sm">({score.percentage}%)</span>
                                                </div>
                                            )}
                                            {teacherGrade != null && gradingSystem !== 'none' && score.total > 0 && (
                                                <p className="text-white/80 text-xs font-medium mt-0.5">Đúng {score.correct}/{score.total} nhiệm vụ</p>
                                            )}
                                        </div>
                                    </div>
                                    {teacherComments && teacherComments.trim() && (
                                        <div className="flex-1 min-w-0 bg-white/15 backdrop-blur-sm rounded-xl lg:rounded-2xl p-4 border border-white/20">
                                            <p className="text-[10px] font-black text-white/70 uppercase tracking-wider mb-1.5">Lời phê</p>
                                            <p className="text-white font-medium text-sm leading-relaxed whitespace-pre-line">{teacherComments.trim()}</p>
                                        </div>
                                    )}
                                </div>
                                {/* Cột phải: Thông điệp khích lệ */}
                                <div className="lg:w-72 shrink-0 flex flex-col justify-center text-center lg:text-left">
                                    <h3 className="text-xl lg:text-2xl font-black text-white uppercase tracking-tight">{cfg.label}</h3>
                                    <p className="text-white/90 text-sm font-medium mt-1.5">{cfg.sublabel}</p>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* 5. Interactive Question Section */}
                {shuffledTasks.length > 0 && (
                    <div className="space-y-6">
                        {shuffledTasks.map((task, index) => (
                            <Card key={task.id} className={`p-6 lg:p-8 overflow-hidden transition-all relative border-slate-100 shadow-xl shadow-slate-200/30 rounded-[2.5rem] ${isSubmitted ? 'border-l-4' : ''} ${isSubmitted && (task.taskType !== HomeworkTaskType.Instruction) ? (calculateScoreForTask(task, answers[task.id]) ? 'border-l-green-500 bg-green-50/10' : 'border-l-red-500 bg-red-50/10') : ''}`}>
                                <div className="flex gap-4 lg:gap-6"><div className="flex-shrink-0 flex items-center justify-center w-8 h-8 lg:w-10 lg:h-10 rounded-2xl bg-teal-600 text-white font-black text-xs lg:text-sm shadow-lg shadow-teal-500/20">{index + 1}</div>
                                    <div className="flex-1 space-y-4">
                                        <div><div className="flex items-center gap-2 mb-2"><span className="text-[10px] font-black uppercase tracking-widest text-teal-600 bg-teal-50 px-2 py-0.5 rounded-lg border border-teal-100">{task.taskType}</span>{task.level && <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">• {task.level}</span>}</div>{task.taskType !== HomeworkTaskType.FillInBlanks && (
                                            <div className={task.imageUrl ? 'grid gap-4 md:grid-cols-[1fr_min(40%,20rem)] lg:grid-cols-[1fr_min(45%,25rem)] items-start lg:gap-8' : ''}>
                                                <div className="text-base lg:text-xl font-black text-slate-800 leading-snug min-w-0 prose prose-slate max-w-none">
                                                    <MathMixedText text={String(task.description ?? '')} whitespaceMode="line" />
                                                </div>
                                                {task.imageUrl ? (
                                                    <div className="rounded-[1.5rem] overflow-hidden border-2 border-slate-100 bg-slate-50 shadow-inner max-w-lg w-full lg:max-w-none mx-auto lg:mx-0 group/img relative shrink-0">
                                                        <img src={formatAssetUrl(task.imageUrl)} alt={task.description} className="w-full h-auto object-contain max-h-[25rem] mx-auto cursor-zoom-in group-hover:scale-105 transition-transform duration-700" onClick={() => setZoomImage(formatAssetUrl(task.imageUrl))} />
                                                        <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none flex items-center justify-center"><EyeIcon className="h-10 w-10 text-white drop-shadow-lg" /></div>
                                                    </div>
                                                ) : null}
                                            </div>
                                        )}</div>
                                        <div className="mt-6 relative">{renderTaskContent(task)}</div>
                                        {isSubmitted && task.taskType !== HomeworkTaskType.Instruction && (task.content?.correctAnswer !== undefined || task.content?.correctAnswerIndex !== undefined || (Array.isArray(task.content?.correctAnswerIndices) && task.content.correctAnswerIndices.length) || task.content?.orderedItems?.length || toPairsArray(task.content?.pairs).length || task.content?.blanks?.length) && (
                                            <div className="mt-4 p-4 rounded-xl bg-emerald-50/80 border border-emerald-100">
                                                <p className="text-[10px] font-black text-emerald-600 uppercase tracking-wider mb-2">Đáp án đúng</p>
                                                {task.taskType === HomeworkTaskType.MultipleChoiceSingle ? (() => {
                                                    const texts = getMcqCorrectAnswerTexts(task.content, task.content?.options || []);
                                                    const opts = task.content?.options || [];
                                                    const t = texts[0];
                                                    const ix = t !== undefined ? opts.indexOf(t) : -1;
                                                    return (
                                                        <p className="font-bold text-emerald-700">
                                                            {ix >= 0 && <span className="mr-2 text-emerald-800">{getMcqOptionLabel(ix)}.</span>}
                                                            <MathMixedText text={String(t ?? task.content?.correctAnswer ?? '')} />
                                                        </p>
                                                    );
                                                })() : task.taskType === HomeworkTaskType.TrueFalse ? (
                                                    <p className="font-bold text-emerald-700"><MathMixedText text={String(task.content.correctAnswer ?? '')} /></p>
                                                ) : task.taskType === HomeworkTaskType.MultipleChoiceMulti ? (
                                                    <div className="flex flex-wrap gap-2">
                                                        {getMcqCorrectAnswerTexts(task.content, task.content?.options || []).map((a: string, i: number) => {
                                                            const opts = task.content?.options || [];
                                                            const ix = opts.indexOf(a);
                                                            return (
                                                                <span key={i} className="px-2 py-1 bg-emerald-100 rounded-lg text-xs font-bold text-emerald-800 inline-flex items-center gap-1">
                                                                    {ix >= 0 && <span className="font-black">{getMcqOptionLabel(ix)}.</span>}
                                                                    <MathMixedText text={String(a)} />
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                ) : task.taskType === HomeworkTaskType.Ordering && task.content?.orderedItems?.length ? (
                                                    <div className="space-y-1">
                                                        {task.content.orderedItems.map((item: string, i: number) => (
                                                            <div key={i} className="flex items-center gap-2 text-xs font-bold text-emerald-700 min-w-0">
                                                                <span className="w-5 h-5 shrink-0 rounded-full bg-emerald-200 flex items-center justify-center text-[10px]">{i + 1}</span>
                                                                <MathMixedText text={String(item)} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : task.taskType === HomeworkTaskType.Matching && toPairsArray(task.content?.pairs).length ? (
                                                    <div className="space-y-1">
                                                        {toPairsArray(task.content.pairs).map((pair: any, i: number) => (
                                                            <div key={i} className="flex flex-wrap items-center gap-2 text-xs min-w-0">
                                                                <span className="font-bold text-emerald-700 min-w-0"><MathMixedText text={String(pair?.left ?? pair ?? '')} /></span>
                                                                <span className="text-emerald-400 shrink-0">→</span>
                                                                <span className="font-bold text-emerald-700 min-w-0"><MathMixedText text={String(pair?.right ?? pair ?? '')} /></span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : task.taskType === HomeworkTaskType.FillInBlanks && task.content?.blanks?.length ? (
                                                    <div className="flex flex-wrap gap-2">
                                                        {task.content.blanks.map((a: string, i: number) => (
                                                            <span key={i} className="px-2 py-1 bg-emerald-100 rounded-lg text-xs font-black text-emerald-800 inline-flex flex-wrap items-baseline gap-1 min-w-0">
                                                                <span className="shrink-0">[{i + 1}]</span>
                                                                <MathMixedText text={String(a)} />
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : null}
                                            </div>
                                        )}
                                        {isSubmitted && (task.taskType === HomeworkTaskType.TrueFalse) && getTaskGuidance(task) && (
                                            <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
                                                <p className="text-[10px] font-black text-slate-500 uppercase tracking-wider mb-2">Giải thích đáp án</p>
                                                <div className="text-sm text-slate-600 italic"><MathMixedText text={getTaskGuidance(task)} whitespaceMode="line" /></div>
                                            </div>
                                        )}
                                        {isSubmitted && TEXT_RESPONSE_TASK_TYPES.includes(task.taskType) && getTaskGuidance(task) && (
                                            <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
                                                <p className="text-[10px] font-black text-slate-500 uppercase tracking-wider mb-2">
                                                    {task.taskType === HomeworkTaskType.Drawing || task.taskType === HomeworkTaskType.ProjectFramework || task.taskType === HomeworkTaskType.Worksheet
                                                        ? 'Gợi ý thực hiện'
                                                        : 'Gợi ý đáp án'}
                                                </p>
                                                <div className="text-sm text-slate-600 italic"><MathMixedText text={getTaskGuidance(task)} whitespaceMode="line" /></div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}

                {/* 6. Unified Submission Section - Only for tasks that require file proofs */}
                {!isViewOnly && !isSubmitted && !isOverdueNoSubmit && isTask && ['photo', 'audio_video', 'any'].includes(submissionType) && (
                    <div className="mt-12 space-y-8 animate-in slide-in-from-bottom-6 duration-700">
                        <div className="relative"><div className="absolute inset-0 flex items-center" aria-hidden="true"><div className="w-full border-t border-slate-200"></div></div><div className="relative flex justify-center"><span className="bg-slate-50 px-6 text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">Khu vực nộp bài</span></div></div>
                        <Card className={`p-10 lg:p-16 border-2 border-dashed transition-all duration-500 rounded-[3rem] flex flex-col items-center text-center group relative overflow-hidden ${isDraggingFile ? 'border-teal-500 bg-teal-50 shadow-2xl scale-[1.02]' : 'border-slate-200 bg-white hover:border-teal-300 hover:shadow-xl'}`} 
                            onDragEnter={handleDragEnterFile}
                            onDragOver={handleDragOverFile} 
                            onDragLeave={handleDragLeaveFile} 
                            onDrop={handleDropFile}>
                            <div className={`w-24 h-24 rounded-[2.5rem] flex items-center justify-center mb-8 transition-all duration-700 shadow-2xl ${isDraggingFile ? 'bg-teal-600 text-white scale-110 rotate-12' : 'bg-slate-50 text-slate-400 group-hover:bg-teal-50 group-hover:text-teal-500 group-hover:rotate-12'}`}><ArrowUpIcon className={`h-12 w-12 ${isDraggingFile ? 'animate-bounce' : ''}`} /></div>
                            <h4 className="text-2xl font-black text-slate-800 mb-3">Gửi minh chứng hoàn thành</h4>
                            <p className="text-slate-400 text-base font-medium mb-10 max-w-sm mx-auto leading-relaxed">{submissionType === 'photo' ? 'Chụp ảnh kết quả và kéo vào đây' : submissionType === 'audio_video' ? 'Kéo tệp Video hoặc Âm thanh vào đây' : 'Kéo tệp minh chứng bất kỳ vào đây'}<br /><span className="text-[11px] opacity-60 uppercase tracking-widest mt-2 block">Tối đa {maxFiles} tệp • Max {maxFileSizeMB}MB/tệp</span></p>
                            <input type="file" ref={fileInputRef} className="hidden" multiple onChange={e => handleFileAddition(e.target.files ? Array.from(e.target.files) : [])} />
                            <div className="flex flex-col items-center gap-6 w-full max-w-md"><button onClick={() => fileInputRef.current?.click()} className="w-full py-5 bg-white border-2 border-slate-100 text-slate-600 font-black text-[11px] uppercase tracking-widest rounded-2xl shadow-sm hover:border-teal-500 hover:text-teal-600 transition-all flex items-center justify-center gap-4 group/btn"><div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center group-hover/btn:bg-teal-50 transition-colors"><PlusIcon className="h-4 w-4" /></div>Chọn tệp từ thiết bị của con</button>
                                {taskAttachments.length > 0 && <div className="grid grid-cols-1 gap-3 w-full animate-in fade-in slide-in-from-top-4">{taskAttachments.map((file, i) => (
                                    <div key={i} className="flex items-center justify-between p-4 bg-teal-50/50 border border-teal-100 rounded-2xl group/file">
                                        <div className="flex items-center gap-4 min-w-0"><div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-md shrink-0 border border-teal-50"><PaperClipIcon className="h-5 w-5 text-teal-600" /></div><div className="min-w-0 text-left"><p className="text-xs font-black text-teal-900 truncate">{file.name}</p><p className="text-[10px] font-bold text-teal-600/60 uppercase">{(file.size / 1024 / 1024).toFixed(1)} MB</p></div></div>
                                        <button onClick={() => setTaskAttachments(prev => prev.filter((_, idx) => idx !== i))} className="p-2 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all"><XMarkIcon className="h-5 w-5" /></button>
                                    </div>
                                ))}</div>}
                            </div>
                        </Card>
                    </div>
                )}

                {/* Footer Credits */}
                <footer className="text-center p-12 text-slate-400 border-t border-slate-100 mt-16"><p className="text-xs font-black uppercase tracking-[0.2em] opacity-40">&copy; {new Date().getFullYear()} Bookai.vn</p><p className="text-[10px] font-bold mt-2 italic">Hệ thống đồng hành học tập thông minh cùng con</p></footer>

                {/* 7. Sticky Submit Button - Ẩn khi quá hạn không cho nộp */}
                {!isViewOnly && !isSubmitted && !isOverdueNoSubmit && onComplete && !isAnnouncement && (
                    <div className="fixed bottom-0 left-0 right-0 z-[60] p-6 bg-gradient-to-t from-slate-50 via-slate-50/95 to-transparent flex justify-center pointer-events-none">
                        <button 
                            onClick={() => (submissionType === 'none' || submissionType === 'check_only') ? doSubmit(true) : doSubmit(false)} 
                            disabled={(!canSubmit && (submissionType !== 'none' && submissionType !== 'check_only')) || !canSubmitByDeadline} 
                            className={`w-full max-w-md font-black py-5 rounded-[2rem] shadow-2xl transition-all pointer-events-auto flex items-center justify-center gap-3 uppercase tracking-[0.2em] text-sm ${(canSubmitByDeadline && (canSubmit || submissionType === 'none' || submissionType === 'check_only')) ? 'bg-teal-600 text-white shadow-teal-500/30 hover:bg-teal-700 hover:-translate-y-1 active:scale-95' : 'bg-slate-200 text-slate-400 cursor-not-allowed opacity-80'}`}
                        >
                            <span className="text-lg">{canSubmitByDeadline ? '🚀' : '🔒'}</span>
                            {!canSubmitByDeadline ? 'ĐÃ QUÁ HẠN NỘP BÀI' : submissionType === 'none' || submissionType === 'check_only' ? SUBMIT_LABELS[assignmentType] : (canSubmit ? (SUBMIT_LABELS[assignmentType] || SUBMIT_LABELS.homework) : `Chưa đủ điều kiện nộp bài (${calculateCompletion()}%)`)}
                        </button>
                    </div>
                )}

                {/* 8. Announcement Seen Button - Ẩn khi quá hạn */}
                {!isViewOnly && !isSubmitted && !isOverdueNoSubmit && onComplete && isAnnouncement && (
                    <div className="fixed bottom-0 left-0 right-0 z-[60] p-6 bg-gradient-to-t from-slate-50 via-slate-50/95 to-transparent flex justify-center pointer-events-none">
                        <button onClick={() => doSubmit(true)} className="w-full max-w-md font-black py-5 rounded-[2rem] shadow-2xl transition-all pointer-events-auto flex items-center justify-center gap-3 uppercase tracking-[0.2em] text-sm bg-teal-600 text-white shadow-teal-500/30 hover:bg-teal-700 hover:-translate-y-1 active:scale-95"><span className="text-lg">👀</span>Xác nhận đã xem</button>
                    </div>
                )}
            </div>

            {/* Zoom Modal */}
            <HomeworkMovementMode
                open={movementModeOpen}
                onClose={() => setMovementModeOpen(false)}
                homeworkId={homework?.id != null ? String(homework.id) : undefined}
                shuffledTasks={shuffledTasks}
                shuffledOptions={shuffledOptions}
                answers={answers}
                onAnswerChange={handleAnswerChange}
                isSubmitted={isSubmitted}
            />

            {zoomImage && (
                <div className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 lg:p-10 animate-in fade-in duration-300" onClick={() => setZoomImage(null)}>
                    <button onClick={() => setZoomImage(null)} className="absolute top-6 right-6 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all border border-white/10 shadow-xl"><XMarkIcon className="h-8 w-8" /></button>
                    <div className="relative max-w-full max-h-full group" onClick={e => e.stopPropagation()}>
                        <img src={zoomImage} alt="Zoomed" className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl animate-in zoom-in-95 duration-500" />
                        <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 flex gap-4 opacity-0 group-hover:opacity-100 transition-opacity"><button onClick={() => { const a = document.createElement('a'); a.href = zoomImage; a.download = 'bookai_image.png'; document.body.appendChild(a); a.click(); document.body.removeChild(a); }} className="px-6 py-3 bg-white text-slate-800 font-black rounded-2xl flex items-center gap-2 shadow-xl hover:bg-slate-50 active:scale-95 transition-all"><ArrowDownIcon className="h-5 w-5" /> Tải ảnh về</button></div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Helper function to calculate score for a specific task
export const calculateScoreForTask = (task: HomeworkTask, studentAnswer: any): boolean => {
    if (!task.content) return true;
    if (task.taskType === HomeworkTaskType.Instruction) return true;
    if (!studentAnswer && task.taskType !== HomeworkTaskType.Instruction) return false;
    
    switch (task.taskType) {
        case HomeworkTaskType.MultipleChoiceSingle: {
            const correctTexts = getMcqCorrectAnswerTexts(task.content, task.content?.options || []);
            return correctTexts.length === 1 && String(studentAnswer) === correctTexts[0];
        }
        case HomeworkTaskType.TrueFalse: return String(studentAnswer) === String(task.content.correctAnswer);
        case HomeworkTaskType.MultipleChoiceMulti: {
            const correctTexts = getMcqCorrectAnswerTexts(task.content, task.content?.options || []);
            const correctSet = new Set(correctTexts.map(String));
            const studentSet = new Set(Array.isArray(studentAnswer) ? studentAnswer.map(String) : []);
            return correctSet.size === studentSet.size && [...correctSet].every(val => studentSet.has(val));
        }
        case HomeworkTaskType.Matching:
            const correctPairs = toPairsArray(task.content?.pairs);
            const studentPairs = Array.isArray(studentAnswer) ? studentAnswer : [];
            return correctPairs.length > 0 && correctPairs.length === studentPairs.length && correctPairs.every(cp => studentPairs.some((sp: any) => String(sp.left) === String(cp.left) && String(sp.right) === String(cp.right)));
        case HomeworkTaskType.Ordering:
            const correctOrder = task.content.orderedItems || [];
            const studentOrder = Array.isArray(studentAnswer) ? studentAnswer : [];
            return correctOrder.length > 0 && correctOrder.length === studentOrder.length && correctOrder.every((item, i) => String(item) === String(studentOrder[i]));
        case HomeworkTaskType.OralResponse:
            const correctText = String(task.content?.correctAnswer || '');
            const studentText = String(studentAnswer || '');
            return calculateSimilarity(studentText, correctText) >= 80;
        case HomeworkTaskType.FillInBlanks:
            const correctBlanks = task.content.blanks || [];
            const studentBlanks = Array.isArray(studentAnswer) ? studentAnswer : [];
            return correctBlanks.length > 0 && correctBlanks.every((b, i) => (b || '').toLowerCase().trim() === (studentBlanks[i] || '').toLowerCase().trim());
        case HomeworkTaskType.Questions:
        case HomeworkTaskType.Writing:
        case HomeworkTaskType.Research:
        case HomeworkTaskType.Reading:
        case HomeworkTaskType.PreClassAssignment:
        case HomeworkTaskType.Drawing:
        case HomeworkTaskType.ProjectFramework:
        case HomeworkTaskType.Worksheet:
            return String(studentAnswer || '').trim().length > 0;
        default: return !!studentAnswer;
    }
};

export default HomeworkViewer;
